import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import {isEmpty} from 'lodash/lang';
import {Tooltip, Button} from '@material-ui/core';
import {Edit as EditIcon, Delete as DeleteIcon, Send as SendIcon} from '@material-ui/icons';
import {
  withKluwellTableWrapper,
  KluwellTableCore,
  SortingManager,
  DeletionConfirmBox,
  ArchivedOnlyTableFilter,
} from 'components/common';
import {buildUserFullName, formatDatetime} from 'utils/common';
import {isReader, isParent} from 'utils/permissions';

class ExtraFilters extends React.Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
  };

  render() {
    const {
      values: {archivedOnly},
      onChange,
    } = this.props;
    return (
      <div className="extra-table-filters">
        <ArchivedOnlyTableFilter archivedOnly={archivedOnly} onChange={onChange} />
      </div>
    );
  }
}

const buildSubscriptionRecords = (subscriptions) => {
  const sortedIds = subscriptions.sortedIds || [];
  return isEmpty(sortedIds)
    ? []
    : sortedIds.map((subscriptionId) => {
        const subscription = subscriptions[subscriptionId];
        const {reader} = subscription;
        return {
          record: subscription,
          id: subscriptionId,
          subscriptionId: subscriptionId,
          email: reader.email,
          username: reader.username,
          school: subscription.school.name,
          classroom: subscription.classroom.name,
          expiryDate: formatDatetime(subscription.expiryDate, false, 'Do MMMM, YYYY'),
          createdBy: buildUserFullName(subscription.createdBy),
          datetimeCreated: formatDatetime(subscription.datetimeCreated, false, 'dddd Do MMMM, YYYY'),
          actions: '',
        };
      });
};

const buildSubscriptionSettings = () => ({
  subscriptionId: {
    fieldText: 'Subscription ID',
    sortable: true,
  },
  email: {
    fieldText: 'Email',
    sortable: true,
  },
  username: {
    fieldText: 'Username',
    sortable: true,
  },
  school: {
    fieldText: 'School',
    formatData: (cell, row) => <Link to={`/schools/${row.record.school.id}`}>{cell}</Link>,
  },
  classroom: {
    fieldText: 'Classroom',
    formatData: (cell, row) => <Link to={`/classrooms/${row.record.classroom.id}`}>{cell}</Link>,
  },
  expiryDate: {
    fieldText: 'Expiry Date',
  },
  createdBy: {
    fieldText: 'Created By',
  },
  datetimeCreated: {
    fieldText: 'Date Created',
    sortable: true,
  },
  actions: {
    fieldText: 'Actions',
    formatData: (cell, row, restProps) => {
      const {reader: user} = row.record;
      return (
        <span className="table-actions">
          <EditIcon className="cursor-pointer" onClick={() => restProps.showUpdateModal(row.record)} />
          {restProps.canDeleteRow && (
            <DeleteIcon className="cursor-pointer" onClick={() => restProps.showDeletionConfirmBox(row)} />
          )}
          {row.email && restProps.resendSubscription && (
            <Tooltip title="Resend Subscription Email" placement="top">
              <SendIcon className="cursor-pointer" onClick={() => restProps.resendSubscription(row)} />
            </Tooltip>
          )}
          <br />
          {restProps.showActivateSubscriptionModal &&
            (isParent(user.role) || (isReader(user.role) && isEmpty(user.firstName) && isEmpty(user.lastName))) && (
              <Button variant="contained" color="primary" onClick={() => restProps.showActivateSubscriptionModal(row)}>
                Edit to Activate
              </Button>
            )}
          {restProps.activateSubscription &&
            isReader(user.role) &&
            !isEmpty(user.firstName) &&
            !isEmpty(user.lastName) && (
              <Button variant="contained" color="primary" onClick={() => restProps.activateSubscription(row)}>
                Activate
              </Button>
            )}
        </span>
      );
    },
  },
});

class PendingSubscriptionTableCore extends React.Component {
  static propTypes = {
    recordPages: PropTypes.object.isRequired,
    onSortableFieldClick: PropTypes.func.isRequired,
    onDeleteActionClick: PropTypes.func,
    showUpdateModal: PropTypes.func.isRequired,
    resendSubscription: PropTypes.func.isRequired,
    showActivateSubscriptionModal: PropTypes.func.isRequired,
    activateSubscription: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      subscriptionToRemove: {},
    };
    this.showDeletionConfirmBox = this.showDeletionConfirmBox.bind(this);
    this.sortingManager = new SortingManager();
  }

  showDeletionConfirmBox(subscription) {
    this.setState(
      {
        subscriptionToRemove: subscription,
      },
      () => {
        ReactDOM.findDOMNode(this.deletionConfirmBoxRef)
          .querySelector('.btn-show-confirm')
          .click();
      },
    );
  }

  render() {
    const {
      recordPages,
      onSortableFieldClick,
      onDeleteActionClick,
      hiddenFields,
      showUpdateModal,
      resendSubscription,
      showActivateSubscriptionModal,
      activateSubscription,
    } = this.props;
    const subscriptions = recordPages[recordPages.currentPage] || {};
    const records = buildSubscriptionRecords(subscriptions);
    const settings = buildSubscriptionSettings();
    return (
      <React.Fragment>
        {onDeleteActionClick && (
          <DeletionConfirmBox
            key="DeletionConfirmBox"
            ref={(ref) => (this.deletionConfirmBoxRef = ref)}
            itemName="subscription"
            deleteAction={() => onDeleteActionClick(this.state.subscriptionToRemove)}
            DeleteButton="span"
          />
        )}
        <KluwellTableCore
          {...{
            key: 'KluwellTableCore',
            records,
            settings,
            sortingManager: this.sortingManager,
            onSortableFieldClick,
            hiddenFields,
            canDeleteRow: !!onDeleteActionClick,
            showDeletionConfirmBox: this.showDeletionConfirmBox,
            showUpdateModal,
            resendSubscription,
            showActivateSubscriptionModal,
            activateSubscription,
          }}
        />
      </React.Fragment>
    );
  }
}

export default withKluwellTableWrapper(PendingSubscriptionTableCore, ExtraFilters);
